import React, { useMemo, useCallback, useState } from 'react';
import {
  useEmailConfigurationDelete,
  useEmailConfigurationGetFilters,
  useEmailConfigurationList,
  useEmailConfigurationTest,
} from '../hooks/use-emailConfiguration.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { EmailConfiguration } from '../types/emailConfiguration.types';
import EmailConfigurationTable from '../components/emailConfiguration-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message, Button } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { WithSysAdminPermissionCheck } from '@components/auth/WithSysAdminPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';
import { maskPassword } from '../../../../../utils/crypto.util';
import EmailConfigurationTestModal from '../components/emailConfiguration-test-modal.component';

import { TestEmailConfigurationPayload } from '../types/emailConfiguration.types';

const EmailConfigurationTableContainer = () => {
  const { t } = useTranslation();
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [selectedEmailConfig, setSelectedEmailConfig] = useState<EmailConfiguration | null>(null);

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } =
    useEmailConfigurationGetFilters();

  const { data, isLoading, isFetching } = useEmailConfigurationList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteEmailConfigurationMutation = useEmailConfigurationDelete();
  const testEmailConfigurationMutation = useEmailConfigurationTest();

  const handleDeleteEmailConfiguration = useCallback(
    async (record: EmailConfiguration) => {
      try {
        await deleteEmailConfigurationMutation.mutateAsync(record.id);
        message.success(t('crud.delete.success', { entity: t('emailConfiguration.title') }));
      } catch (error) {
        const errorMessage = extractCreateError(
          error,
          t('crud.delete.error', { entity: t('emailConfiguration.title') })
        );
        message.error(errorMessage);
      }
    },
    [deleteEmailConfigurationMutation, t]
  );

  const handleTestConfiguration = useCallback((record: EmailConfiguration) => {
    setSelectedEmailConfig(record);
    setTestModalVisible(true);
  }, []);

  const handleTestModalCancel = useCallback(() => {
    setTestModalVisible(false);
    setSelectedEmailConfig(null);
  }, []);

  const handleSendTestEmail = useCallback(
    async (email: string) => {
      if (!selectedEmailConfig) return;

      try {
        const payload: TestEmailConfigurationPayload = {
          testEmail: email,
        };

        await testEmailConfigurationMutation.mutateAsync(payload);

        message.success(t('emailConfiguration.test.success'));
        setTestModalVisible(false);
        setSelectedEmailConfig(null);
      } catch (error: any) {
        console.error('Test email failed:', error);
        const errorMessage = extractCreateError(error, 'emailConfiguration.test.error');
        message.error(errorMessage);
      }
    },
    [selectedEmailConfig, testEmailConfigurationMutation, t]
  );

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<EmailConfiguration>[]>(
    () => [
      {
        title: t('emailConfiguration.email.label'),
        key: 'email',
        dataIndex: 'email',
      },
      {
        title: t('emailConfiguration.host.label'),
        key: 'host',
        dataIndex: 'host',
      },
      {
        title: t('emailConfiguration.port.label'),
        key: 'port',
        dataIndex: 'port',
      },
      {
        title: t('emailConfiguration.password.label'),
        key: 'password',
        dataIndex: 'password',
        render: (password: string) => maskPassword(password),
      },
      {
        title: t('emailConfiguration.isSslTls.label'),
        key: 'isSslTls',
        dataIndex: 'isSslTls',
        render: (isSslTls: boolean | null | undefined) => (isSslTls ? 'Yes' : 'No'),
      },
      {
        title: t('emailConfiguration.testConfig.label'),
        key: 'testConfig',
        align: 'center',
        render: (_: any, record: EmailConfiguration) => (
          <Button type="primary" size="small" onClick={() => handleTestConfiguration(record)}>
            Test Email
          </Button>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_: any, record: EmailConfiguration) => (
          <ActionButtons
            record={record}
            baseUrl="/dashboard/admin/emailConfiguration"
            onDelete={handleDeleteEmailConfiguration}
            isDeleting={
              deleteEmailConfigurationMutation.isPending &&
              deleteEmailConfigurationMutation.variables === record.id
            }
          />
        ),
      },
    ],
    [
      t,
      handleDeleteEmailConfiguration,
      handleTestConfiguration,
      deleteEmailConfigurationMutation.isPending,
      deleteEmailConfigurationMutation.variables,
    ]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('emailConfiguration.title') })}
        createRoute="/dashboard/admin/emailConfiguration/create"
        isSysAdmin={true}
        showCreateButton={true}
      />
      <div className="row mt-3">
        <EmailConfigurationTable
          data={tableData}
          columns={columns}
          loading={isLoading || isFetching}
          pagination={data}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>

      {selectedEmailConfig && (
        <EmailConfigurationTestModal
          visible={testModalVisible}
          onCancel={handleTestModalCancel}
          onSendTestEmail={handleSendTestEmail}
          loading={testEmailConfigurationMutation.isPending}
        />
      )}
    </>
  );
};

export default WithSysAdminPermissionCheck(
  EmailConfigurationTableContainer,
  'emailConfiguration.view'
);
